'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Cookie, X } from 'lucide-react';
import {
  getStoredAnalyticsConsent,
  setAnalyticsConsent,
  ANALYTICS_CONSENT_SETTINGS_EVENT,
  type AnalyticsConsentValue,
} from '@/lib/analyticsConsent';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [storedChoice, setStoredChoice] = useState<AnalyticsConsentValue | null>(null);

  useEffect(() => {
    const stored = getStoredAnalyticsConsent();
    setStoredChoice(stored);
    if (!stored) {
      // Small delay so it doesn't flash immediately on load
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const handleOpenSettings = () => {
      setStoredChoice(getStoredAnalyticsConsent());
      setVisible(true);
    };
    window.addEventListener(ANALYTICS_CONSENT_SETTINGS_EVENT, handleOpenSettings);
    return () => window.removeEventListener(ANALYTICS_CONSENT_SETTINGS_EVENT, handleOpenSettings);
  }, []);

  // Reopened via a "Cookie Settings" control (footer / settings page) rather than
  // the first-visit prompt -- reflect the existing choice instead of asking fresh.
  const isReview = storedChoice !== null;

  const handleAccept = () => {
    setAnalyticsConsent('accepted');
    setStoredChoice('accepted');
    setVisible(false);
  };

  const handleDecline = () => {
    setAnalyticsConsent('declined');
    setStoredChoice('declined');
    setVisible(false);
  };

  // On the first-visit prompt, dismissing without choosing is treated as a decline
  // (the safer default). When reopened for review, dismissing just closes the panel
  // without changing the existing choice.
  const handleDismiss = isReview ? () => setVisible(false) : handleDecline;

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6">
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-2xl p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Cookie size={18} className="text-blue-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900 text-base mb-1">
              {isReview ? 'Cookie settings' : 'We use cookies'}
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              {isReview ? (
                <>
                  Analytics cookies are currently{' '}
                  <strong>{storedChoice === 'accepted' ? 'enabled' : 'disabled'}</strong> for this
                  browser. Essential cookies for authentication always stay on.{' '}
                </>
              ) : (
                <>
                  Ertzyx uses essential cookies for authentication and optional analytics cookies to
                  improve the platform. By clicking &quot;Accept&quot;, you consent to all cookies.
                  You can decline non-essential cookies without affecting core functionality.{' '}
                </>
              )}
              <Link href="/cookie-policy" className="text-blue-700 underline hover:text-blue-800">
                Cookie Policy
              </Link>{' '}
              ·{' '}
              <Link href="/privacy-policy" className="text-blue-700 underline hover:text-blue-800">
                Privacy Policy
              </Link>
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0 mt-0.5"
            aria-label={isReview ? 'Close' : 'Decline and close'}
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-5 sm:justify-end">
          <button
            onClick={handleDecline}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            {isReview ? 'Withdraw consent' : 'Decline Non-Essential'}
          </button>
          <button
            onClick={handleAccept}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-slate-900 hover:bg-slate-700 transition-colors"
          >
            {isReview ? 'Grant analytics consent' : 'Accept All Cookies'}
          </button>
        </div>
      </div>
    </div>
  );
}
